import { Component } from '@theme/component';
import { getCart, isPairedProductInCart, isRemovedUpsellId } from '@theme/cart-upsell-state';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { isClickedOutside, onAnimationEnd, preloadImage, ensureImageWidth } from '@theme/utilities';

/**
 * @typedef {object} AtcSuccessPopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} fullContent
 * @property {HTMLElement} abbreviatedContent
 * @property {HTMLElement} messageEl
 * @property {HTMLElement} productEl
 * @property {HTMLElement} viewCartWrap
 * @property {HTMLElement} viewCartButton
 * @property {HTMLElement} upsellEl
 * @property {HTMLElement} messageElAbbr
 * @property {HTMLElement} viewCartLinkAbbr
 * @property {HTMLElement} closeButton
 * @property {HTMLElement} innerHandle
 */

/**
 * @typedef {object} LineItemShape
 * @property {string} [product_handle]
 * @property {string} [handle]
 * @property {string} [title]
 * @property {string | null} [image]
 * @property {string} [variant_title]
 * @property {{ url?: string }} [featured_image]
 */

/**
 * @typedef {{ lineItem: LineItemShape | null, message: string }} AtcPayload
 */

/**
 * @typedef {Object} CartUpdateEventDetail
 * @property {string} [sourceId]
 * @property {Object} [data]
 * @property {string} [data.variantId]
 * @property {boolean} [data.didError]
 * @property {string} [data.source]
 * @property {{ productHandle?: string; productTitle?: string; variantTitle?: string; sizeLabel?: string; imageUrl?: string | null }} [data.productInfo]
 * @property {{ pairedProductId: string; pairedProductHandle: string; title: string; imageUrl: string | null; upsellText: string; upsellButtonText: string }} [data.upsellInfo]
 */
/** @typedef {Event & { detail?: CartUpdateEventDetail }} CartUpdateEventLike */

const MOBILE_BREAKPOINT = 750;
/** Min downward swipe distance (px) to close the ATC popup on mobile. */
const SWIPE_DOWN_THRESHOLD = 60;
const DEFAULT_SIZE_MESSAGE = 'Size {{ size }} added to cart';
/** Section key in product template (sections/atc-popup-upsell.liquid) */
const ATC_POPUP_UPSELL_SECTION_ID = 'atc_popup_upsell';
/** Delay between cart fetch retries when payload is missing (no productInfo path). */
const CART_RETRY_DELAY_MS = 80;

/** Known add-to-cart sources; exclude cart-items-component (quantity updates) so popup does not open on cart page quantity change. */
const CART_ADD_SOURCES = new Set([
  'product-form-component',
  'quick-add',
  'quick-order-quantity',
  'quick-order-remove-all',
  'shop-the-look-ingrid',
]);

/**
 * Returns true if the event is a successful add-to-cart we should show the popup for.
 * Accepts CartAddEvent instances or any event with the same detail shape (e.g. when instanceof fails due to module context).
 * @param {CartUpdateEventLike} event
 */
function isCartAddEvent(event) {
  if (event.type !== ThemeEvents.cartUpdate) return false;
  const sourceId = event.detail?.sourceId ?? event.detail?.data?.variantId;
  if (!sourceId || event.detail?.data?.didError === true) return false;
  const source = event.detail?.data?.source;
  if (source === 'cart-items-component') return false;
  if (source === 'mini-pdp-shop-the-look') return false;
  return event instanceof CartAddEvent || !source || CART_ADD_SOURCES.has(source);
}

function getSizeMessageTemplate() {
  const t =
    typeof Theme !== 'undefined' && Theme.translations?.size_added_to_cart;
  return (typeof t === 'string' && t) ? t : DEFAULT_SIZE_MESSAGE;
}

function getAddedToCartMessage() {
  const t = typeof Theme !== 'undefined'
    ? Theme.translations?.products?.product?.added_to_cart
    : undefined;
  return (typeof t === 'string' && t) ? t : 'Added to cart';
}

/** Returns true if the size value represents "one size" and should not be shown in the message. */
function isOneSize(size) {
  const ONE_SIZE_VALUES = new Set(['OS', 'O/S', 'OSFM', 'ONE SIZE', 'ONE-SIZE', 'ONESIZE']);
  return ONE_SIZE_VALUES.has(size.trim().toUpperCase());
}

/** Derive size-only label from full variant title (e.g. "Ecru / 26" -> "26") for cart fallback. */
/**
 * @param {string | null | undefined} variantTitle
 */
function getSizeForMessage(variantTitle) {
  if (variantTitle == null || typeof variantTitle !== 'string') return '';
  const trimmed = variantTitle.trim();
  const lastSlash = trimmed.lastIndexOf(' / ');
  return lastSlash >= 0 ? trimmed.slice(lastSlash + 3).trim() : trimmed;
}


/**
 * Returns true if the fetched upsell HTML should be shown in the popup.
 * Hides upsell if paired product is already in cart (server returns no upsell) or if customer previously added then removed the paired product.
 * @param {string} html - Section inner HTML
 * @returns {{ show: boolean, pairedProductId: string | null }}
 */
function shouldShowUpsellInPopup(html) {
  if (!html || typeof html !== 'string' || !html.trim()) return { show: false, pairedProductId: null };
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const upsellEl = doc.querySelector('cart-upsell-component');
  if (!upsellEl) return { show: false, pairedProductId: null };
  const pairedId = upsellEl.getAttribute('data-paired-product-id')?.trim();
  if (!pairedId) return { show: true, pairedProductId: null };
  if (isRemovedUpsellId(pairedId)) return { show: false, pairedProductId: pairedId };
  return { show: true, pairedProductId: pairedId };
}

/**
 * Add to cart success popup (full and abbreviated).
 * Listens for CartAddEvent, shows full popup (desktop top-right / mobile bottom sheet) or abbreviated bar (mobile In-Grid Shop the Look).
 * @extends Component<AtcSuccessPopupRefs>
 */
class AtcSuccessPopupComponent extends Component {
  requiredRefs = [
    'dialog',
    'fullContent',
    'abbreviatedContent',
    'messageEl',
    'productEl',
    'viewCartWrap',
    'viewCartButton',
    'upsellEl',
    'messageElAbbr',
    'viewCartLinkAbbr',
    'closeButton',
    'innerHandle',
  ];

  #previousScrollY = 0;
  /** @type {string | null} Cached dynamic section ID resolved from a product page fetch (used when not on product page). */
  #resolvedUpsellSectionId = null;
  #touchStartY = 0;
  #touchStartScrollTop = 0;
  /** @type {((e: TouchEvent) => void) | null} */
  #scrollLockTouchHandler = null;
  #boundClose = () => this.#closeDialog();
  #boundViewCart = () => this.#handleViewCart();
  /** @param {MouseEvent} e */
  #boundDialogClick = (e) => this.#handleDialogClick(e);
  /** @param {KeyboardEvent} e */
  #boundKeyDown = (e) => this.#handleKeyDown(e);
  /** @param {TouchEvent} e */
  #boundSwipeTouchStart = (e) => this.#handleSwipeTouchStart(e);
  /** @param {TouchEvent} e */
  #boundSwipeTouchMove = (e) => this.#handleSwipeTouchMove(e);
  /** @param {TouchEvent} e */
  #boundSwipeTouchEnd = (e) => this.#handleSwipeTouchEnd(e);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.refs.closeButton?.addEventListener('click', this.#boundClose);
    this.refs.innerHandle?.addEventListener('click', this.#boundClose);
    this.refs.viewCartButton?.addEventListener('click', this.#boundViewCart);
    this.refs.viewCartLinkAbbr?.addEventListener('click', this.#boundViewCart);
    this.refs.dialog?.addEventListener('click', this.#boundDialogClick);
    this.refs.dialog?.addEventListener('keydown', this.#boundKeyDown);
    const fullContent = this.refs.fullContent;
    if (fullContent) {
      fullContent.addEventListener('touchstart', this.#boundSwipeTouchStart, { passive: true });
      fullContent.addEventListener('touchmove', this.#boundSwipeTouchMove, { passive: false });
      fullContent.addEventListener('touchend', this.#boundSwipeTouchEnd, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.refs.closeButton?.removeEventListener('click', this.#boundClose);
    this.refs.innerHandle?.removeEventListener('click', this.#boundClose);
    this.refs.viewCartButton?.removeEventListener('click', this.#boundViewCart);
    this.refs.viewCartLinkAbbr?.removeEventListener('click', this.#boundViewCart);
    this.refs.dialog?.removeEventListener('click', this.#boundDialogClick);
    this.refs.dialog?.removeEventListener('keydown', this.#boundKeyDown);
    const fullContent = this.refs.fullContent;
    if (fullContent) {
      fullContent.removeEventListener('touchstart', this.#boundSwipeTouchStart);
      fullContent.removeEventListener('touchmove', this.#boundSwipeTouchMove);
      fullContent.removeEventListener('touchend', this.#boundSwipeTouchEnd);
    }
  }

  /** @param {CartUpdateEventLike} event */
  #handleCartUpdate = async (event) => {
    if (!isCartAddEvent(event)) return;

    const data = event.detail?.data;
    if (data?.didError === true) return;

    // Do not show popup when ATC came from mini-pdp opened from shop-the-look drawer (they get the drawer toast instead).
    const target = event.target;
    if (target instanceof Element && target.closest?.('[data-from-shop-the-look]')) return;

    const variantId = event.detail?.sourceId || data?.variantId;
    if (!variantId || this.dataset.showPopup !== 'true') return;

    // Kick off image preload after guard checks so we only fetch for events that will show the popup.
    const earlyImageUrl = ensureImageWidth(data?.productInfo?.imageUrl);
    if (earlyImageUrl) preloadImage(earlyImageUrl);

    const isAbbreviated =
      window.innerWidth < MOBILE_BREAKPOINT &&
      (data?.source || '') === 'shop-the-look-ingrid';

    const productInfo = data?.productInfo;

    if (productInfo?.productHandle) {
      const sizeLabelTrimmed =
        productInfo.sizeLabel != null ? String(productInfo.sizeLabel).trim() : '';
      const sizeForMessage =
        sizeLabelTrimmed !== ''
          ? sizeLabelTrimmed
          : (productInfo.variantTitle != null ? String(productInfo.variantTitle) : '');
      const rawSizeValue = getSizeForMessage(productInfo.variantTitle);
      const message = isOneSize(rawSizeValue)
        ? getAddedToCartMessage()
        : getSizeMessageTemplate().replace(/\{\{\s*size\s*\}\}/g, sizeForMessage);
      const lineItem = {
        product_handle: productInfo.productHandle,
        handle: productInfo.productHandle,
        title: productInfo.productTitle ?? '',
        image: productInfo.imageUrl ?? null,
        variant_title: productInfo.variantTitle ?? '',
      };
      const initialPayload = { lineItem, message, upsellInfo: data?.upsellInfo };
      if (isAbbreviated) {
        this.#renderAbbreviated(initialPayload);
        this.#openDialog();
      } else {
        // Don't await — open immediately with product info already available (message + image).
        // Image was already preloaded at the top of this handler.
        // Upsell section fetch happens in background and injects when ready.
        this.#renderFull(initialPayload);
        this.#openDialog();
      }
      return;
    }

    const addedTranslation = typeof Theme !== 'undefined'
      ? Theme.translations?.products?.product?.added_to_cart
      : undefined;
    const fallbackMessage = typeof addedTranslation === 'string' ? addedTranslation : 'Added to cart';
    const fallbackPayload = { lineItem: null, message: fallbackMessage };
    if (isAbbreviated) {
      this.#renderAbbreviated(fallbackPayload);
    } else {
      this.#renderFull(fallbackPayload);
    }
    this.#openDialog();

    const { payload, cart } = await this.#getPayloadWithRetry(variantId);
    if (!payload) return;
    if (isAbbreviated) {
      this.#renderAbbreviated(payload);
    } else {
      this.#renderFull(payload, cart);
    }
  };

  /** @param {string | number} variantId
   * @returns {Promise<{ payload: AtcPayload | null, cart: { items?: unknown[] } | null }>} */
  async #getPayloadWithRetry(variantId) {
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const cart = await getCart();
      const payload = this.#getPayloadFromCart(cart, variantId);
      if (payload?.lineItem) return { payload, cart };
      if (attempt < maxAttempts - 1) await new Promise((r) => setTimeout(r, CART_RETRY_DELAY_MS));
      else return { payload: payload ?? null, cart };
    }
    return { payload: null, cart: null };
  }

  /**
   * @param {{ items?: Array<{ variant_id?: string | number; variant_title?: string; title?: string; [key: string]: unknown }> }} cart
   * @param {string | number} variantId
   * @returns {AtcPayload}
   */
  #getPayloadFromCart(cart, variantId) {
    const lineItem = this.#findLineItemByVariantId(cart, variantId);
    const rawSizeText = lineItem ? (lineItem.variant_title || lineItem.title || '') : '';
    const sizeText = (getSizeForMessage(rawSizeText) || rawSizeText || '').trim();
    const message = isOneSize(sizeText)
      ? getAddedToCartMessage()
      : getSizeMessageTemplate().replace(/\{\{\s*size\s*\}\}/g, sizeText);
    return { lineItem: lineItem || null, message };
  }

  /** @param {AtcPayload} payload */
  #renderAbbreviated(payload) {
    const { message } = payload;
    this.refs.fullContent.hidden = true;
    this.refs.abbreviatedContent.hidden = false;
    this.refs.messageElAbbr.textContent = message;
    this.refs.viewCartLinkAbbr.hidden = this.dataset.showViewCart !== 'true';
    this.refs.dialog?.classList.add('atc-success-popup__dialog--abbreviated');
  }

  /** Sets full popup UI state: View Cart visibility and non-abbreviated dialog class. */
  #setFullPopupState() {
    this.refs.viewCartWrap.hidden = this.dataset.showViewCart !== 'true';
    this.refs.dialog?.classList.remove('atc-success-popup__dialog--abbreviated');
  }

  /**
   * @param {AtcPayload & { upsellInfo?: { pairedProductId: string; pairedProductHandle: string; title: string; imageUrl: string | null; upsellText: string; upsellButtonText: string } }} payload
   * @param {{ items?: Array<{ product_id?: string | number }> } | { items?: unknown[] } | null} [optionalCart] - Reuse cart when already fetched (e.g. from getPayloadWithRetry) to avoid extra /cart.js.
   * @returns {Promise<void> | undefined} Resolves when upsell fetch (if any) has completed; undefined if no upsell fetch started.
   */
  async #renderFull(payload, optionalCart = null) {
    const { lineItem, message, upsellInfo } = payload;
    this.refs.abbreviatedContent.hidden = true;
    this.refs.fullContent.hidden = false;
    this.refs.messageEl.textContent = message;
    this.#setFullPopupState();

    if (lineItem) {
      const rawImageUrl = lineItem.image || lineItem.featured_image?.url;
      const imageUrl = ensureImageWidth(rawImageUrl);
      this.refs.productEl.hidden = !imageUrl;
      this.refs.productEl.innerHTML = imageUrl
        ? `<img src="${this.#escapeHtml(imageUrl)}" alt="${this.#escapeHtml(lineItem.title || '')}" loading="eager" width="200" height="200">`
        : '';
      this.#clearUpsell();
      const handle = lineItem.product_handle || lineItem.handle;
      if (!handle) return;

      if (upsellInfo?.pairedProductId && upsellInfo?.pairedProductHandle) {
        // upsellInfo contains all data from product metafields — render natively without any
        // section fetches. Fetch cart once here and pass it through to avoid a duplicate call.
        const cart = optionalCart ?? (await getCart());
        return this.#renderNativeUpsell(
          upsellInfo,
          /** @type {{ items?: Array<{ product_id?: string | number }> }} */ (cart)
        );
      }
      return this.#fetchAndInjectSectionUpsell(handle);
    } else {
      this.refs.productEl.hidden = true;
      this.refs.productEl.innerHTML = '';
      this.#clearUpsell();
    }
  }

  /** Hides and clears the upsell container. */
  #clearUpsell() {
    if (!this.refs.upsellEl) return;
    this.refs.upsellEl.hidden = true;
    this.refs.upsellEl.innerHTML = '';
    this.refs.upsellEl.classList.remove('atc-success-popup__upsell--visible');
  }

  /**
   * Resolves section ID if needed, fetches section HTML, and injects upsell if allowed (fallback for quick-add etc.).
   * @param {string} productHandle
   * @returns {Promise<void>}
   */
  async #fetchAndInjectSectionUpsell(productHandle) {
    if (this.#getUpsellSectionId() === ATC_POPUP_UPSELL_SECTION_ID) {
      const resolvedId = await this.#resolveUpsellSectionIdFromProductPage(productHandle);
      if (resolvedId) this.#resolvedUpsellSectionId = resolvedId;
    }
    const html = await this.#fetchUpsellSectionHtml(productHandle);
    this.#injectUpsellIfAllowed(html);
  }

  /**
   * Renders upsell in the popup from upsellInfo (product metafields). Uses shared getCart or optionalCart to avoid extra /cart.js.
   * @param {{ pairedProductId: string; pairedProductHandle: string; title: string; imageUrl: string | null; upsellText: string; upsellButtonText: string }} upsellInfo
   * @param {{ items?: Array<{ product_id?: string | number }> } | { items?: unknown[] } | null} [optionalCart] - Reuse when already fetched (e.g. from getPayloadWithRetry).
   * @returns {Promise<void>}
   */
  async #renderNativeUpsell(upsellInfo, optionalCart = null) {
    if (!this.refs.upsellEl) return;
    if (isRemovedUpsellId(upsellInfo.pairedProductId)) {
      this.#clearUpsell();
      return;
    }
    const cart = optionalCart ?? (await getCart());
    if (isPairedProductInCart(/** @type {{ items?: Array<{ product_id?: string | number }> } } */ (cart), upsellInfo.pairedProductId)) {
      this.#clearUpsell();
      return;
    }
    // Markup matches snippets/cart-upsell.liquid (cart-upsell__trigger, __content, __text, __title, __cta, __image) for consistent styling.
    const upsellTitleRaw =
      typeof Theme !== 'undefined' && Theme.translations?.upsell_title;
    const upsellTitle =
      typeof upsellTitleRaw === 'string' ? upsellTitleRaw : 'Make it a set';
    const subtitle = upsellInfo.upsellText ? `<p class="cart-upsell__subtitle">${this.#escapeHtml(upsellInfo.upsellText)}</p>` : '';
    const upsellImageUrl = ensureImageWidth(upsellInfo.imageUrl, 450);
    const imageBlock =
      upsellImageUrl
        ? `<div class="cart-upsell__image"><span class="cart-upsell__image-link"><img src="${this.#escapeHtml(upsellImageUrl)}" alt="${this.#escapeHtml(upsellInfo.title)}" loading="lazy" class="cart-upsell__image-element" width="113" height="130"></span></div>`
        : '';
    const handleAttr = this.#escapeHtml(upsellInfo.pairedProductHandle);
    const html = `
      <button type="button" class="cart-upsell__trigger" data-product-handle="${handleAttr}">
        <div class="cart-upsell__content">
          <div class="cart-upsell__text">
            <h3 class="cart-upsell__title">${this.#escapeHtml(upsellTitle)}</h3>
            ${subtitle}
            <span class="cart-upsell__cta" data-product-handle="${handleAttr}">${this.#escapeHtml(upsellInfo.upsellButtonText)}</span>
          </div>
          ${imageBlock}
        </div>
      </button>`;
    this.#showUpsellContent(html);
  }

  /**
   * Injects section upsell HTML only when allowed (paired product not in "removed" list). Used for quick-add etc. fallback.
   * @param {string | null} upsellHtml
   */
  #injectUpsellIfAllowed(upsellHtml) {
    if (!this.refs.upsellEl) return;
    const { show } = shouldShowUpsellInPopup(upsellHtml ?? '');
    if (!show || !upsellHtml || !upsellHtml.trim()) {
      this.#clearUpsell();
      return;
    }
    this.#showUpsellContent(upsellHtml);
  }

  /**
   * Renders HTML into the upsell container, reveals it, and delegates CTA (close + navigate).
   * @param {string} html
   */
  #showUpsellContent(html) {
    if (!html || !this.refs.upsellEl) return;
    this.refs.upsellEl.innerHTML = html;
    this.refs.upsellEl.hidden = false;
    requestAnimationFrame(() => {
      this.refs.upsellEl?.classList.add('atc-success-popup__upsell--visible');
    });
    this.#delegateUpsellCta(this.refs.upsellEl);
  }

  /**
   * @param {{ items?: Array<{ variant_id?: string | number; variant_title?: string; title?: string; [key: string]: unknown }> }} cart
   * @param {string | number} variantId
   * @returns {LineItemShape | null}
   */
  #findLineItemByVariantId(cart, variantId) {
    const id = String(variantId);
    const items = cart?.items || [];
    return items.find((item) => String(item.variant_id) === id) || items[items.length - 1] || null;
  }

  /**
   * Resolves the section ID for the atc-popup-upsell section from the DOM or cache.
   * Prefers cached ID (from a prior product-page resolve), then data-atc-upsell-section-id in DOM, then wrapper id.
   * @returns {string} Section ID to use for the Section Rendering API, or fallback constant.
   */
  #getUpsellSectionId() {
    if (this.#resolvedUpsellSectionId) return this.#resolvedUpsellSectionId;

    const dataEl = document.querySelector('[data-atc-upsell-section-id]');
    const dataId = dataEl?.getAttribute('data-atc-upsell-section-id')?.trim();
    if (dataId) return dataId;

    const el = document.querySelector(
      '[id^="shopify-section-"][id*="atc_popup_upsell"]'
    );
    if (el?.id) return el.id.replace(/^shopify-section-/, '');
    return ATC_POPUP_UPSELL_SECTION_ID;
  }

  /**
   * Fetches the product page HTML and extracts the atc-popup-upsell section ID from data-atc-upsell-section-id.
   * Caches the result so the product template's section ID is reused for subsequent upsell fetches.
   * @param {string} productHandle
   * @returns {Promise<string | null>} The dynamic section ID, or null if not found.
   */
  async #resolveUpsellSectionIdFromProductPage(productHandle) {
    if (this.#resolvedUpsellSectionId) return this.#resolvedUpsellSectionId;
    try {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = window.location.origin + routeRoot + 'products/' + encodeURIComponent(productHandle);
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const dataEl = doc.querySelector('[data-atc-upsell-section-id]');
      const id = dataEl?.getAttribute('data-atc-upsell-section-id')?.trim();
      if (id) {
        this.#resolvedUpsellSectionId = id;
        return id;
      }
    } catch {
      // ignore
    }
    return null;
  }

  /** @param {string} productHandle @returns {Promise<string | null>} */
  async #fetchUpsellSectionHtml(productHandle) {
    try {
      const sectionId = this.#getUpsellSectionId();
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(window.location.origin + routeRoot + 'products/' + encodeURIComponent(productHandle));
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) return null;

      const sectionHtml = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const sectionEl =
        doc.getElementById('shopify-section-' + sectionId) ||
        doc.querySelector('.shopify-section');
      let inner = sectionEl?.innerHTML?.trim() || '';
      if (!inner) {
        inner = doc.body?.innerHTML?.trim() || sectionHtml.trim() || '';
      }
      return inner || null;
    } catch {
      return null;
    }
  }

  /** @param {HTMLElement} container */
  #delegateUpsellCta(container) {
    const trigger = container.querySelector('.cart-upsell__trigger');
    const cta = container.querySelector('[data-upsell-cta]') || container.querySelector('.cart-upsell__cta');
    const btn = trigger || cta;
    const handle =
      trigger?.getAttribute('data-product-handle')?.trim() ||
      cta?.getAttribute('data-product-handle')?.trim();
    if (!btn) return;
    const hasMiniPdpTemplate = btn.querySelector?.('template[data-mini-pdp-content]');
    btn.addEventListener('click', async () => {
      await this.#closeDialog();
      if (hasMiniPdpTemplate) {
        const drawer = document.querySelector('mini-pdp-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open({ target: btn });
        } else if (handle) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}products/${handle}`;
        }
      } else if (handle) {
        await this.#openMiniPdpByHandle(handle);
      }
    });
  }

  /**
   * Fetches mini-PDP content for a product and opens the mini-PDP drawer.
   * Falls back to navigation if the drawer or content is unavailable.
   * @param {string} handle
   */
  async #openMiniPdpByHandle(handle) {
    const drawer = /** @type {{ open?: (e: { target: HTMLElement }) => void } | null} */ (
      document.querySelector('mini-pdp-drawer')
    );
    if (!drawer || typeof drawer.open !== 'function') {
      window.location.href = `${window.Shopify?.routes?.root || '/'}products/${handle}`;
      return;
    }

    const html = await this.#fetchMiniPdpContent(handle);
    if (!html) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}products/${handle}`;
      return;
    }

    const trigger = document.createElement('div');
    trigger.dataset.productHandle = handle;
    const template = document.createElement('template');
    template.setAttribute('data-mini-pdp-content', '');
    template.innerHTML = html;
    trigger.appendChild(template);

    drawer.open({ target: trigger });
  }

  /**
   * Fetches pre-rendered mini-PDP content via the Section Rendering API.
   * Loads module scripts so custom elements are registered before rendering.
   * @param {string} handle
   * @returns {Promise<string | null>}
   */
  async #fetchMiniPdpContent(handle) {
    try {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(`${routeRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'section-rendering-mini-pdp');
      const response = await fetch(url.toString());
      if (!response.ok) return null;
      const rawHtml = await response.text();
      const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
      const scripts = doc.querySelectorAll('script[type="module"][src]');
      const imports = [];
      for (const script of scripts) {
        const src = script.getAttribute('src');
        if (!src) continue;
        const tagName = src.match(/\/([a-z-]+)\.js/)?.[1];
        if (tagName && customElements.get(tagName)) continue;
        imports.push(import(src).catch(() => {}));
      }
      if (imports.length > 0) await Promise.all(imports);
      const wrapper = doc.querySelector('.shopify-section');
      return wrapper ? wrapper.innerHTML : rawHtml;
    } catch {
      return null;
    }
  }

  /** @param {string | null | undefined} str */
  #escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }

  #openDialog() {
    const dialog = this.refs.dialog;
    if (!dialog || dialog.open) return;
    this.#previousScrollY = window.scrollY;
    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      try {
        dialog.show();
      } catch {
        // ignore
      }
      return;
    }
    this.#applyScrollLock();
    try {
      dialog.showModal();
    } catch {
      this.#removeScrollLock();
      const other = document.querySelector('dialog[open]');
      if (other && other !== dialog && 'close' in other) {
        /** @type {HTMLDialogElement} */ (other).close();
      }
      try {
        this.#applyScrollLock();
        dialog.showModal();
      } catch {
        this.#removeScrollLock();
      }
    }
  }

  #applyScrollLock() {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (!this.#scrollLockTouchHandler) {
      this.#scrollLockTouchHandler = (e) => {
        if (e.target instanceof Element && e.target.closest('dialog')) return;
        e.preventDefault();
      };
      document.addEventListener('touchmove', this.#scrollLockTouchHandler, { passive: false });
    }
  }

  #removeScrollLock() {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (this.#scrollLockTouchHandler) {
      document.removeEventListener('touchmove', this.#scrollLockTouchHandler);
      this.#scrollLockTouchHandler = null;
    }
  }

  #closeDialog = async () => {
    const dialog = this.refs.dialog;
    if (!dialog || !dialog.open) return;
    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    this.#removeScrollLock();
    if (window.innerWidth < MOBILE_BREAKPOINT && !document.documentElement.hasAttribute('data-scrolling-to-top')) {
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }
    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  #handleViewCart = async () => {
    await this.#closeDialog();
    const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.href = this.dataset.cartUrl || '/cart';
    }
  };

  /** @param {MouseEvent} event */
  #handleDialogClick(event) {
    const dialog = this.refs.dialog;
    if (dialog && isClickedOutside(event, dialog)) this.#closeDialog();
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDialog();
    }
  }

  #isMobileFullPopup() {
    const dialog = this.refs.dialog;
    return dialog?.open && window.innerWidth < MOBILE_BREAKPOINT &&
      !dialog.classList.contains('atc-success-popup__dialog--abbreviated');
  }

  /** @param {TouchEvent} e */
  #handleSwipeTouchStart(e) {
    if (!this.#isMobileFullPopup() || !e.touches?.length) return;
    const touch = e.touches[0];
    if (touch) this.#touchStartY = touch.clientY;
    const fullContent = this.refs.fullContent;
    this.#touchStartScrollTop = (fullContent && fullContent.parentElement?.scrollTop) ?? 0;
  }

  /** @param {TouchEvent} e */
  #handleSwipeTouchMove(e) {
    if (!this.#isMobileFullPopup() || this.#touchStartScrollTop > 5) return;
    const deltaY = e.touches[0].clientY - this.#touchStartY;
    if (deltaY <= 0) return;
    e.preventDefault();
    this.refs.dialog.style.transform = `translateY(${deltaY}px)`;
  }

  /** @param {TouchEvent} e */
  #handleSwipeTouchEnd(e) {
    if (!this.#isMobileFullPopup() || !e.changedTouches?.length) return;
    const dialog = this.refs.dialog;
    const deltaY = e.changedTouches[0].clientY - this.#touchStartY;
    if (this.#touchStartScrollTop <= 10 && deltaY >= SWIPE_DOWN_THRESHOLD) {
      dialog.style.transform = '';
      this.#closeDialog();
    } else {
      dialog.style.transition = 'transform 0.2s ease';
      dialog.style.transform = '';
      dialog.addEventListener('transitionend', () => { dialog.style.transition = ''; }, { once: true });
    }
  }
}

if (!customElements.get('atc-success-popup-component')) {
  customElements.define('atc-success-popup-component', AtcSuccessPopupComponent);
}
